/**
 * CSK Square and Closure algebra — a reusable, UI-independent model of the
 * "squares" that recur across the repo: forbidden square families, double-
 * category squares, closure-derived composite squares, and matrix cells.
 *
 * The module is finite and decidable: {@link enumerateClosure} grows the union
 * closure of a finite bridge alphabet under horizontal composition, checks each
 * derivable composite against a forbid predicate, and returns the first
 * counterexample (if any) without ever discarding the derivation tree.
 *
 * Theorem metadata, when a square is theorem-governed, is attached through
 * `NeutralizationSecurityGuarantee` rather than code/name strings. No React,
 * layout coordinates, or app-specific route ids appear here.
 *
 * Agda source: Foundation/Square/, SI-C1 (Sealed Surface Law)
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { Brand } from "./types.js";
import type { NeutralizationSecurityGuarantee } from "../shared/canonical.js";

// ── Identifiers and boundaries ─────────────────────────────────

/** A square identifier — branded to prevent accidental substitution. */
export type SquareId = Brand<string, "SquareId">;

/** Construct a square identifier. */
export function squareId(value: string): SquareId {
  return value as SquareId;
}

/** Whether a square is forbidden, admitted (inhabited), or closure-derived. */
export type SquareKind = "forbidden" | "inhabited" | "composite";

/**
 * The four edges of a square, each labeled by an object of the ambient
 * category. Top/bottom are the horizontal edges; left/right are the vertical
 * edges.
 */
export interface SquareBoundary<Obj> {
  readonly top: Obj;
  readonly bottom: Obj;
  readonly left: Obj;
  readonly right: Obj;
}

// ── Theorem-governed squares ───────────────────────────────────

/**
 * A theorem-governed square: a boundary plus an optional interior witness and
 * the guarantee that governs it. Specialized by {@link ForbiddenSquare} and
 * {@link InhabitedSquare}.
 */
export interface CskSquare<Obj, W = unknown> {
  readonly id: SquareId;
  readonly kind: SquareKind;
  readonly boundary: SquareBoundary<Obj>;
  readonly interior: W | null;
  readonly guarantee: NeutralizationSecurityGuarantee | null;
}

/** A square class made uninhabited by construction — no interior can exist. */
export interface ForbiddenSquare<Obj> extends CskSquare<Obj, never> {
  readonly kind: "forbidden";
  readonly interior: null;
}

/** An admitted square carrying proof-relevant interior witness data. */
export interface InhabitedSquare<Obj, W> extends CskSquare<Obj, W> {
  readonly kind: "inhabited";
  readonly interior: W;
}

/** Build a forbidden square (an uninhabited 2-cell class). */
export function forbiddenSquare<Obj>(
  id: string,
  boundary: SquareBoundary<Obj>,
  guarantee: NeutralizationSecurityGuarantee | null = null,
): ForbiddenSquare<Obj> {
  return { id: squareId(id), kind: "forbidden", boundary, interior: null, guarantee };
}

/** Build an inhabited square from its boundary and interior witness. */
export function inhabitedSquare<Obj, W>(
  id: string,
  boundary: SquareBoundary<Obj>,
  interior: W,
  guarantee: NeutralizationSecurityGuarantee | null = null,
): InhabitedSquare<Obj, W> {
  return { id: squareId(id), kind: "inhabited", boundary, interior, guarantee };
}

/** Type guard: is this a forbidden (uninhabited) square? */
export function isForbiddenSquare<Obj>(square: CskSquare<Obj>): square is ForbiddenSquare<Obj> {
  return square.kind === "forbidden";
}

// ── Closure-derived composite squares ──────────────────────────

/**
 * The derivation tree of a composite square. `base` wraps a single alphabet
 * morphism; `horizontal`/`vertical` record binary composition; `whisker`
 * records pre-/post-composition with a single morphism on one side. The tree is
 * preserved so that closure evidence is never lost.
 */
export type SquareDerivation<M> =
  | { readonly kind: "base"; readonly morphism: M }
  | {
      readonly kind: "horizontal";
      readonly left: SquareDerivation<M>;
      readonly right: SquareDerivation<M>;
    }
  | {
      readonly kind: "vertical";
      readonly upper: SquareDerivation<M>;
      readonly lower: SquareDerivation<M>;
    }
  | {
      readonly kind: "whisker";
      readonly base: SquareDerivation<M>;
      readonly side: "left" | "right" | "top" | "bottom";
      readonly morphism: M;
    };

/**
 * A closure-derived composite square: the path of component morphisms, its
 * source/target objects (the vertical edges traversed by the composite), and
 * the derivation tree that built it.
 */
export interface CompositeSquare<Obj, M> {
  readonly id: SquareId;
  readonly source: Obj;
  readonly target: Obj;
  readonly morphisms: readonly M[];
  readonly derivation: SquareDerivation<M>;
  readonly guarantee: NeutralizationSecurityGuarantee | null;
}

/**
 * The finite alphabet a closure is enumerated over. `source`/`target` read an
 * object off a morphism; `composable` is the horizontal-match predicate (the
 * channel/seam discipline lives here); `forbidden` decides whether a derived
 * composite inhabits the forbidden class.
 */
export interface ClosureAlphabet<Obj, M> {
  readonly morphisms: readonly M[];
  readonly source: (morphism: M) => Obj;
  readonly target: (morphism: M) => Obj;
  readonly composable: (left: M, right: M) => boolean;
  readonly forbidden: (square: CompositeSquare<Obj, M>) => boolean;
  /** Optional guarantee tagged onto every derived square. */
  readonly guarantee?: NeutralizationSecurityGuarantee;
}

/** Coverage statistics for an enumerated closure. */
export interface ClosureCoverage {
  readonly totalSquares: number;
  readonly forbiddenCount: number;
  readonly maxLength: number;
  /** Count of derived squares keyed by composite length. */
  readonly byLength: Readonly<Record<number, number>>;
}

/**
 * The result of enumerating a closure: every derivable square, the forbidden
 * subset, the first counterexample found (if any), and coverage stats.
 */
export interface ClosureReport<Obj, M> {
  readonly squares: readonly CompositeSquare<Obj, M>[];
  readonly forbidden: readonly CompositeSquare<Obj, M>[];
  readonly firstCounterexample: CompositeSquare<Obj, M> | null;
  readonly coverage: ClosureCoverage;
}

/** Wrap a single morphism as a base composite square. */
export function baseSquare<Obj, M>(
  alphabet: ClosureAlphabet<Obj, M>,
  morphism: M,
  idValue: string,
): CompositeSquare<Obj, M> {
  return {
    id: squareId(idValue),
    source: alphabet.source(morphism),
    target: alphabet.target(morphism),
    morphisms: [morphism],
    derivation: { kind: "base", morphism },
    guarantee: alphabet.guarantee ?? null,
  };
}

/**
 * Horizontally compose two composite squares when the left's last morphism is
 * composable with the right's first. Returns `null` when the match fails.
 */
export function composeHorizontal<Obj, M>(
  alphabet: ClosureAlphabet<Obj, M>,
  left: CompositeSquare<Obj, M>,
  right: CompositeSquare<Obj, M>,
): CompositeSquare<Obj, M> | null {
  const leftLast = left.morphisms[left.morphisms.length - 1];
  const rightFirst = right.morphisms[0];
  if (leftLast === undefined || rightFirst === undefined) return null;
  if (!alphabet.composable(leftLast, rightFirst)) return null;
  return {
    id: squareId(`${String(left.id)}∘${String(right.id)}`),
    source: left.source,
    target: right.target,
    morphisms: [...left.morphisms, ...right.morphisms],
    derivation: { kind: "horizontal", left: left.derivation, right: right.derivation },
    guarantee: alphabet.guarantee ?? null,
  };
}

/**
 * Vertically stack two composite squares with matching source/target objects.
 * Models the double-category vertical composition; returns `null` on mismatch.
 */
export function composeVertical<Obj, M>(
  alphabet: ClosureAlphabet<Obj, M>,
  upper: CompositeSquare<Obj, M>,
  lower: CompositeSquare<Obj, M>,
  equals: (a: Obj, b: Obj) => boolean = Object.is,
): CompositeSquare<Obj, M> | null {
  if (!equals(upper.source, lower.source) || !equals(upper.target, lower.target)) {
    return null;
  }
  return {
    id: squareId(`${String(upper.id)}⊟${String(lower.id)}`),
    source: upper.source,
    target: upper.target,
    morphisms: [...upper.morphisms, ...lower.morphisms],
    derivation: { kind: "vertical", upper: upper.derivation, lower: lower.derivation },
    guarantee: alphabet.guarantee ?? null,
  };
}

/**
 * Enumerate the horizontal closure of an alphabet up to a composite-length
 * bound. Mirrors the Consumed-by-Construction closure: base squares (length 1)
 * grow into longer composites by horizontal composition, and the derivation
 * tree is retained on every square so the report carries full closure evidence.
 *
 * `maxLength` is the maximum composite length; the minimum effective length is
 * 1 (the base squares). A non-finite, fractional, or `< 1` bound admits no
 * squares and yields an empty report.
 */
export function enumerateClosure<Obj, M>(
  alphabet: ClosureAlphabet<Obj, M>,
  maxLength: number,
): ClosureReport<Obj, M> {
  if (!Number.isFinite(maxLength) || !Number.isInteger(maxLength) || maxLength < 1) {
    return {
      squares: [],
      forbidden: [],
      firstCounterexample: null,
      coverage: {
        totalSquares: 0,
        forbiddenCount: 0,
        maxLength: 0,
        byLength: {},
      },
    };
  }

  const base = alphabet.morphisms.map((morphism, index) =>
    baseSquare(alphabet, morphism, `b${String(index)}`),
  );
  const all: CompositeSquare<Obj, M>[] = [...base];
  let frontier: CompositeSquare<Obj, M>[] = base;

  for (let length = 2; length <= maxLength; length += 1) {
    const next: CompositeSquare<Obj, M>[] = [];
    for (const right of frontier) {
      for (const left of all) {
        if (left.morphisms.length + right.morphisms.length !== length) continue;
        const composed = composeHorizontal(alphabet, left, right);
        if (composed !== null) next.push(composed);
      }
    }
    if (next.length === 0) break;
    all.push(...next);
    frontier = next;
  }

  const forbidden = all.filter((square) => alphabet.forbidden(square));
  const byLength: Record<number, number> = {};
  for (const square of all) {
    const length = square.morphisms.length;
    byLength[length] = (byLength[length] ?? 0) + 1;
  }

  return {
    squares: all,
    forbidden,
    firstCounterexample: forbidden[0] ?? null,
    coverage: {
      totalSquares: all.length,
      forbiddenCount: forbidden.length,
      maxLength,
      byLength,
    },
  };
}
